import axios from 'axios';
import getBackendUrl, { failoverToNextHealthy } from './backendUrl';
import { getToken, isTokenExpired, clearToken } from './tokenManager';

// For localhost development, POS backend runs on port 8002
// For production, it uses the same URL as the main backend
const getPOSBackendUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8002';
  }
  return getBackendUrl();
};

// Create axios instance — `baseURL` is resolved per-request via the request
// interceptor below so failover (Railway → Render → Fly → Emergent) takes
// effect within the SAME page session, without a refresh.
const posApi = axios.create({
  baseURL: getPOSBackendUrl(),
});

// Refresh the baseURL before every request so a failover that just happened
// is picked up immediately.
posApi.interceptors.request.use((config) => {
  config.baseURL = getPOSBackendUrl();
  return config;
});

// Add request interceptor to include auth token
posApi.interceptors.request.use(
  (config) => {
    // Check if token is expired before making request
    if (isTokenExpired()) {
      console.warn('⚠️ Token expired - will redirect to login');
      clearToken();
      // Use setTimeout to prevent blocking the current execution context
      // This prevents race conditions that can cause blank pages
      setTimeout(() => {
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }, 100);
      return Promise.reject(new Error('Token expired'));
    }
    
    // Get token from localStorage
    const token = getToken();
    
    if (token) {
      // Add token in Authorization header (preferred method)
      config.headers.Authorization = `Bearer ${token}`;
      
      // Also add token as query parameter for endpoints that need it
      // This ensures compatibility with all endpoint authentication methods
      const url = new URL(config.url, config.baseURL || window.location.origin);
      if (!url.searchParams.has('token')) {
        config.params = config.params || {};
        config.params.token = token;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor:
//   - 401 → clear token + redirect to /login
//   - Network errors / 502/503/504 → trigger backend failover (Feb 2026
//     multi-host redundancy). The next request from the page hits the
//     new healthy host transparently. We DO NOT retry the same request
//     here automatically (avoids double-submit on mutating calls) — the
//     caller's existing retry/error handling kicks in, and by then the
//     new baseURL is already cached.
let _failoverInFlight = null;
const _maybeFailover = (error) => {
  const status = error?.response?.status;
  const code = error?.code;
  const isNetErr = !error.response &&
    (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT');
  const is5xx = [502, 503, 504].includes(status);
  if (!isNetErr && !is5xx) return;
  if (_failoverInFlight) return;
  _failoverInFlight = failoverToNextHealthy()
    .catch(() => {})
    .finally(() => { _failoverInFlight = null; });
};

posApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('❌ Authentication failed - will redirect to login');
      clearToken();
      setTimeout(() => {
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }, 100);
    } else if (error.response?.status === 402) {
      // Free-tier cap reached. Open the upgrade modal instead of showing
      // a raw axios error toast. Backend payload shape (see
      // pos-backend/services/plan_limits.py):
      //   { detail: { error: 'free_tier_orders_cap_reached' | 'free_tier_menu_cap_reached',
      //               message, cap, used, upgrade_plan, upgrade_price } }
      try {
        const detail = error.response?.data?.detail || {};
        const isCap = typeof detail.error === 'string' && detail.error.startsWith('free_tier_');
        if (isCap && typeof window !== 'undefined') {
          const reason = detail.error.includes('order') ? 'orders_cap' : 'items_cap';
          window.dispatchEvent(new CustomEvent('flavorfeed-open-upgrade-modal', {
            detail: { reason, message: detail.message },
          }));
        }
      } catch (_e) { /* non-browser or malformed payload */ }
    } else {
      _maybeFailover(error);
    }
    return Promise.reject(error);
  }
);

// Cash Drawer Management API Methods
posApi.getCurrentCashDrawer = async () => {
  const response = await posApi.get('/api/pos/cash-drawer/current');
  return response.data;
};

posApi.getLastClosingBalance = async () => {
  const response = await posApi.get('/api/pos/cash-drawer/last-closing');
  return response.data;
};

posApi.openCashDrawer = async (data) => {
  const formData = new FormData();
  formData.append('opening_balance', data.opening_balance);
  formData.append('cashier_name', data.cashier_name);
  if (data.notes) formData.append('notes', data.notes);
  if (data.auto_close_hours) formData.append('auto_close_hours', data.auto_close_hours);
  
  const response = await posApi.post('/api/pos/cash-drawer/open', formData);
  return response.data;
};

posApi.closeCashDrawer = async (data) => {
  const formData = new FormData();
  formData.append('actual_cash', data.actual_cash);
  if (data.denominations) formData.append('denominations', data.denominations);
  if (data.closing_notes) formData.append('closing_notes', data.closing_notes);
  formData.append('override_variance', data.override_variance || false);
  
  const response = await posApi.post('/api/pos/cash-drawer/close', formData);
  return response.data;
};

posApi.cashDrawerCashIn = async (data) => {
  const formData = new FormData();
  formData.append('amount', data.amount);
  formData.append('reason', data.reason);
  if (data.notes) formData.append('notes', data.notes);
  
  const response = await posApi.post('/api/pos/cash-drawer/cash-in', formData);
  return response.data;
};

posApi.cashDrawerCashOut = async (data) => {
  const formData = new FormData();
  formData.append('amount', data.amount);
  formData.append('reason', data.reason);
  if (data.notes) formData.append('notes', data.notes);
  
  const response = await posApi.post('/api/pos/cash-drawer/cash-out', formData);
  return response.data;
};

posApi.getCashTransactions = async (sessionId = null) => {
  const url = sessionId 
    ? `/api/pos/cash-drawer/transactions?session_id=${sessionId}`
    : '/api/pos/cash-drawer/transactions';
  const response = await posApi.get(url);
  return response.data;
};

posApi.getCashDrawerHistory = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await posApi.get(`/api/pos/cash-drawer/history?${params}`);
  return response.data;
};

posApi.getCashDrawerShiftDetail = async (sessionId) => {
  const response = await posApi.get(`/api/pos/cash-drawer/shift-detail/${sessionId}`);
  return response.data;
};

posApi.getCashTrail = async (filters = {}) => {
  const params = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v !== '' && v !== null && v !== undefined)
  ).toString();
  const response = await posApi.get(`/api/pos/cash-drawer/cash-trail?${params}`);
  return response.data;
};

posApi.exportCashDrawerHistoryCsv = async (filters = {}) => {
  // Returns the raw axios response (with blob body) so the caller can
  // trigger a browser download via createObjectURL.
  const params = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v !== '' && v !== null && v !== undefined)
  ).toString();
  const response = await posApi.get(
    `/api/pos/cash-drawer/history/export.csv?${params}`,
    { responseType: 'blob' },
  );
  return response;
};

// ─── Reconciliation (Phase 3, Feb 2026) ─────────────────────────────
// Surfaces data-integrity issues to admin/owners so they can fix
// problems before they snowball into financial mistakes.
posApi.getReconciliationIssues = async (branchId = null) => {
  const params = new URLSearchParams();
  if (branchId) params.set('branch_id', branchId);
  const response = await posApi.get(`/api/pos/reconciliation/issues?${params.toString()}`);
  return response.data;
};

posApi.runReconciliationHeal = async () => {
  const response = await posApi.post('/api/pos/reconciliation/heal-now');
  return response.data;
};

posApi.recomputeShift = async (sessionId = null) => {
  const formData = new FormData();
  if (sessionId) formData.append('session_id', sessionId);
  const response = await posApi.post('/api/pos/cash-drawer/recompute', formData);
  return response.data;
};

posApi.transferCashDrawer = async (data) => {
  const formData = new FormData();
  formData.append('new_cashier_name', data.new_cashier_name);
  if (data.handover_notes) formData.append('handover_notes', data.handover_notes);
  
  const response = await posApi.post('/api/pos/cash-drawer/transfer', formData);
  return response.data;
};

posApi.getReceiptSettings = async () => {
  const response = await posApi.get('/api/pos/receipt/settings');
  return response.data;
};

// Recipe Management API Methods
posApi.getRecipes = async () => {
  const response = await posApi.get('/api/pos/recipes');
  return response.data;
};

posApi.getRecipe = async (recipeId) => {
  const response = await posApi.get(`/api/pos/recipes/${recipeId}`);
  return response.data;
};

posApi.createRecipe = async (data) => {
  const response = await posApi.post('/api/pos/recipes', data);
  return response.data;
};

posApi.updateRecipe = async (recipeId, data) => {
  const response = await posApi.put(`/api/pos/recipes/${recipeId}`, data);
  return response.data;
};

posApi.deleteRecipe = async (recipeId) => {
  const response = await posApi.delete(`/api/pos/recipes/${recipeId}`);
  return response.data;
};

posApi.deductRecipeInventory = async (recipeId, portions) => {
  const response = await posApi.post(`/api/pos/recipes/${recipeId}/deduct-inventory`, { portions });
  return response.data;
};

export default posApi;
